import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EmpLayout from "./emp_layout";
import { API_ENDPOINTS } from "../../api/config";
import './css/candidate_profile.css';

export function CandidateSearchProfile() {
  const navigate = useNavigate();
  const { candidateId } = useParams(); 
  
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContact, setShowContact] = useState(false); // Track contact details visibility
  const token = localStorage.getItem("token");

  // Alert/Message custom modal state fallback
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmLabel: "",
    variant: "primary",
    onConfirm: () => {}
  });

  // Fetch candidate details via the target User ID path
  const fetchCandidateDetails = useCallback(() => {
    if (!candidateId) {
      setError("No candidate ID provided.");
      setLoading(false);
      return;
    }

    // Hit the newly generated direct user profile route
    fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/user/${candidateId}`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch candidate professional profile.");
      return res.json();
    })
    .then((data) => {
      if (!data) throw new Error("Candidate profile data returned empty.");
      setCandidate(data);
      setLoading(false);
    })
    .catch((err) => {
      console.error("Error fetching candidate profile:", err);
      setError(err.message);
      setLoading(false);
    });
  }, [candidateId, token]);

  useEffect(() => {
    fetchCandidateDetails();
  }, [fetchCandidateDetails]);

  // View CV handler
  const handleViewCV = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/view-search-cv/${candidateId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Session expired or unauthorized access.");
        throw new Error("Could not locate the document asset on the server.");
      }

      const fileBlob = await response.blob();
      const secureBlobUrl = window.URL.createObjectURL(fileBlob);
      window.open(secureBlobUrl, "_blank");
      
      setTimeout(() => window.URL.revokeObjectURL(secureBlobUrl), 200);      
    } catch (error) {
      openActionModal("Error", error.message || "An error occurred.", "Close", "danger", () => closeActionModal());
    }
  };

  const openActionModal = (title, message, confirmLabel, variant, onConfirm) => {
    setActionModal({ isOpen: true, title, message, confirmLabel, variant, onConfirm });
  };

  const closeActionModal = () => {
    setActionModal(prev => ({ ...prev, isOpen: false }));
  };

  if (loading) {
    return (
      <EmpLayout title="Talent" titleEm="Profile">
        <div style={{ padding: "2rem", textAlign: "center", color: "#aaa" }}>
          Loading candidate professional profile...
        </div>
      </EmpLayout>
    );
  }

  if (error || !candidate) {
    return (
      <EmpLayout title="Talent" titleEm="Profile">
        <div style={{ padding: "2rem", textAlign: "center", color: "#d9534f" }}>
          <p>{error || "Candidate professional profile record could not be found."}</p>
          <button className="emp-btn-ghost" onClick={() => navigate(-1)}>Back to Search</button>
        </div>
      </EmpLayout>
    );
  }

  return (
    <EmpLayout title="Talent" titleEm="Profile ✦">
      <button 
        style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:"0.78rem", display:"flex", alignItems:"center", gap:"0.4rem", padding:0, marginBottom:"1.25rem" }}
        onClick={() => navigate(-1)}
      >
        ← Back to exploration
      </button>

      <div className="cp-layout">
        {/* Left Side: Profile Details */}
        <div>
          {/* Hero segment */}
          <div className="cp-hero">
            <div className="cp-big-av">
              {candidate.name 
                ? candidate.name.trim().split(/\s+/).map(n => n[0]).join("").toUpperCase() 
                : "??"}
            </div>
            <div className="cp-hero-info">
              <div className="cp-hero-name">{candidate.name || "Anonymous Professional"}</div>
              <div className="cp-hero-spec">
                {candidate.headline || `${candidate.specialisation || "N/A"} · ${candidate.experience || "No"} experience`}
              </div>
              <div className="cp-hero-tags">
                <span className="cp-hero-tag">📍 {candidate.location || "Flexible / Remote"}</span>
                {candidate.availability && (
                  <span className="cp-hero-tag gold">{candidate.availability}</span>
                )}
              </div>
            </div>
          </div>

          {/* About Section */}
          <div className="cp-section">
            <div className="cp-section-label">Professional Summary</div>
            <p className="cp-bio">{candidate.bio || "No professional overview provided."}</p>
          </div>

          {/* Technical Details Grid */}
          <div className="cp-section">
            <div className="cp-section-label">Details</div>
            <div className="cp-grid2">
              {[
                ["Specialisation", candidate.specialisation],
                ["Experience", candidate.experience],
                ["Location", candidate.location],
                ["Availability", candidate.availability],
              ].map(([k, v]) => (
                <div key={k} className="cp-detail">
                  <div className="cp-detail-l">{k}</div>
                  <div className="cp-detail-v">{v || "N/A"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Core Skills Inventory */}
          <div className="cp-section">
            <div className="cp-section-label">Core Skills</div>
            <div className="cp-skill-wrap">
              {Array.isArray(candidate.skills) && candidate.skills.length > 0 ? (
                candidate.skills.map(s => (
                  <span key={s} className="cp-skill">{s}</span>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.85rem" }}>No structural core competencies listed.</span>
              )}
            </div>
          </div>

          {/* Certifications Display Module */}
          <div className="cp-section">
            <div className="cp-section-label">Certifications & Credentials</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {Array.isArray(candidate.certificates) && candidate.certificates.length > 0 ? (
                candidate.certificates.map((cert, index) => (
                  <div key={cert.id || index} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", background: "rgba(var(--color-text-rgb),0.02)", padding: "0.85rem 1rem", borderRadius: "6px" }}>
                    <span style={{ fontSize: "1.2rem", lineHeight: 1 }}>📜</span>
                    <div>
                      <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "var(--color-text)" }}>{cert.certificateName || "Certified Qualification"}</div>
                      <div style={{ fontSize: "0.78rem", color: "#aaa", marginTop: "0.15rem" }}>{cert.issuingOrganization || "Unknown Institution"}</div>
                    </div>
                  </div>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.85rem", fontStyle: "italic" }}>No validated certifications listed.</span>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Document Side Actions & Contact Information */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          
          {/* Action Card: Contact Information Toggle */}
          <div className="cp-sidebar-card">
            <div className="cp-sidebar-title">Contact Information</div>
            
            {!showContact ? (
              <button
                type="button"
                onClick={() => setShowContact(true)} 
                className="cp-action-btn primary" 
                style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: 0, backgroundColor: "var(--color-text)", color: "#fff" }}
              >
                📞 View Contact Details
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                  <span style={{ fontSize: "0.72rem", color: "#aaa", textTransform: "uppercase", fontWeight: 600 }}>Email Address</span>
                  <a href={`mailto:${candidate.email}`} style={{ fontSize: "0.88rem", color: "var(--color-primary)", textDecoration: "none", wordBreak: "break-all" }}>
                    {candidate.email || "No email available"}
                  </a>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", borderTop: "1px dashed rgba(0,0,0,0.08)", paddingTop: "0.5rem" }}>
                  <span style={{ fontSize: "0.72rem", color: "#aaa", textTransform: "uppercase", fontWeight: 600 }}>Phone Number</span>
                  <a href={`tel:${candidate.phone || candidate.phoneNumber}`} style={{ fontSize: "0.88rem", color: "var(--color-text)", textDecoration: "none" }}>
                    {candidate.phone || candidate.phoneNumber || "No phone number available"}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Action Card: Documents */}
          <div className="cp-sidebar-card">
            <div className="cp-sidebar-title">Documents</div>
            
            {candidate.cvUrl ? (
              <button
                type="button"
                onClick={handleViewCV} 
                className="cp-action-btn primary" 
                style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: 0, backgroundColor: "var(--color-primary)", color: "#fff" }}
              >
                📄 View Full CV / Resume
              </button>
            ) : (
              <button className="cp-action-btn ghost" disabled style={{ opacity: 0.5, cursor: "not-allowed", margin: 0 }}>
                📄 No CV Document Uploaded
              </button>
            )}
          </div>

        </div>
      </div>

      {/* Info Alert Notification Modal */}
      {actionModal.isOpen && (
        <div className="cp-schedule-modal-bg" onClick={closeActionModal}>
          <div className="cp-schedule-modal" style={{ maxWidth: "440px" }} onClick={e => e.stopPropagation()}>
            <div className="cp-schedule-title" style={{ fontSize: "1.4rem", marginBottom: "0.5rem" }}>
              {actionModal.title}
            </div>
            <p style={{ color: "#4a4a4a", fontSize: "0.9rem", lineHeight: "1.5", marginBottom: "1.5rem" }}>
              {actionModal.message}
            </p>
            <div className="cp-modal-actions" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
              <button type="button" className="emp-btn-gold" style={{ backgroundColor: "var(--color-primary)", color: "#fff" }} onClick={actionModal.onConfirm}>
                {actionModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </EmpLayout>
  );
}